/* eslint-disable no-dupe-else-if */
/* eslint-disable no-useless-catch */
/* eslint-disable react/prop-types */
import { useEffect, useRef } from 'react';
import proj4 from 'proj4';
import { useLeafletContext } from '@react-leaflet/core';
import { useMap } from 'react-leaflet';
import parseGeoraster from 'georaster';
import GeoRasterLayer from 'georaster-layer-for-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet-side-by-side';
import geoblaze from 'geoblaze';
import {
  multiPopoverFormatter,
  pixelValuesToColorFn
} from 'helpers/leaflet-utils';
import { isEmpty } from 'lodash';

window.proj4 = proj4;

const GeotiffLayerSideBySide = ({
  leftUrl,
  rightUrl,
  title,
  coordinates,
  indexScale,
  varients,
  layerVisible
}) => {
  const layersRef = useRef([]);
  const context = useLeafletContext();
  const map = useMap();
  const sideBySideControlRef = useRef(null);
  const prevUrlsRef = useRef({ left: null, right: null });
  const abortControllerRef = useRef(new AbortController());

  const isMobileBrowser = /Mobi|Android|iPad|iPhone|iPod/i.test(
    navigator.userAgent
  );

  // const countLayersOnMap = () => {
  //   let layerCount = 0;
  //   map.eachLayer(layer => {
  //     if (layer instanceof GeoRasterLayer) {
  //       layerCount++;
  //     }
  //   });
  //   console.log(
  //     `Current number of GeoRaster layers on the map: ${layerCount}`,
  //     leftUrl,
  //     rightUrl
  //   );
  //   return layerCount;
  // };

  const removeAllGeoRasterLayers = () => {
    const container = context.layerContainer || context.map;
    map.eachLayer(layer => {
      if (layer instanceof GeoRasterLayer) {
        container.removeLayer(layer);
        // console.log('Removed a GeoRasterLayer');
      }
    });
    layersRef.current = [];
  };

  useEffect(() => {
    // console.log('Effect running with URLs:', leftUrl, rightUrl);

    // if (
    //   prevUrlsRef.current.left === leftUrl &&
    //   prevUrlsRef.current.right === rightUrl
    // ) {
    //   // console.log("URLs haven't changed, skipping effect");
    //   return;
    // }

    // console.log('URLs changed, running effect');
    prevUrlsRef.current = { left: leftUrl, right: rightUrl };

    removeAllGeoRasterLayers();

    abortControllerRef.current.abort(); // Cancel any ongoing fetches
    abortControllerRef.current = new AbortController();

    const container = context.layerContainer || context.map;

    // console.log(leftUrl, rightUrl, 'geotiff layer');

    const fetchData = async () => {
      try {
        const responses = await Promise.all([
          fetch(leftUrl, { signal: abortControllerRef.current.signal }),
          fetch(rightUrl, { signal: abortControllerRef.current.signal })
        ]);

        if (responses.every(response => response.ok)) {
          const arrayBuffers = await Promise.all(
            responses.map(response => response.arrayBuffer())
          );
          const georasters = await Promise.all(
            arrayBuffers.map(arrayBuffer => parseGeoraster(arrayBuffer))
          );

          const leftLayer = new GeoRasterLayer({
            georaster: georasters[0],
            resolution: 256,
            opacity: layerVisible ? 0.5 : 0,
            name: 'left_layer',
            pixelValuesToColorFn: values =>
              pixelValuesToColorFn(values[0], title, indexScale)
          });

          const rightLayer = new GeoRasterLayer({
            georaster: georasters[1],
            resolution: 256,
            opacity: layerVisible ? 0.5 : 0,
            name: 'right_layer',
            pixelValuesToColorFn: values =>
              pixelValuesToColorFn(values[0], title, indexScale)
          });

          container.addLayer(leftLayer);
          container.addLayer(rightLayer);
          layersRef.current = [leftLayer, rightLayer];
          // countLayersOnMap();

          map.on('click', function (e) {
            const isControl =
              e.originalEvent.target.closest('.leaflet-control');
            const isSideBySideControl =
              e.originalEvent.target.className === 'leaflet-sbs-range';

            if (isControl || isSideBySideControl) {
              return;
            }

            const latlng = map.mouseEventToLatLng(e.originalEvent);
            const pixelValues = georasters.map(georaster =>
              geoblaze.identify(georaster, [latlng.lng, latlng.lat])
            );

            if (pixelValues.every(pixelValue => pixelValue !== null)) {
              const pixelColors = pixelValues.map(pixelValue =>
                pixelValuesToColorFn(pixelValue[0], title, indexScale)
              );

              const legends = pixelColors.map(
                pixelColor =>
                  indexScale.find(
                    obj =>
                      obj.bg_color?.toLowerCase() === pixelColor?.toLowerCase()
                  )?.legend
              );

              const popupContent = multiPopoverFormatter(
                latlng,
                varients.map((varient, i) => ({
                  varient,
                  color: pixelColors[i],
                  legend: legends[i]
                }))
              );

              if (popupContent) {
                L.popup()
                  .setLatLng(e.latlng)
                  .setContent(popupContent)
                  .openOn(map);
              }
            }
          });

          const combinedLayers = L.layerGroup([leftLayer, rightLayer]);

          if (sideBySideControlRef.current) {
            sideBySideControlRef.current.remove();
          }

          sideBySideControlRef.current = L.control.sideBySide(
            leftLayer,
            rightLayer
          );

          if (layerVisible) {
            sideBySideControlRef.current.addTo(map);
          }

          map.fitBounds(combinedLayers.getBounds());
        } else {
          throw new Error('Response not ok');
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          // console.log('Fetch aborted');
        } else {
          // console.error('Fetch error:', error);
          map.fitBounds(coordinates);
        }
      } finally {
        if (isMobileBrowser) {
          map.dragging.disable();
          map.tapHold.disable();
        }
      }
    };

    fetchData();

    return () => {
      // console.log('Running cleanup');
      abortControllerRef.current.abort();
      removeAllGeoRasterLayers();
      if (sideBySideControlRef.current) {
        sideBySideControlRef.current.remove();
      }
      map.off('click');
      map.closePopup();
    };
  }, [
    context,
    leftUrl,
    rightUrl,
    map,
    layerVisible,
    title,
    indexScale,
    varients,
    coordinates
  ]);

  useEffect(() => {
    if (!isEmpty(layersRef.current)) {
      layersRef.current.forEach(layer =>
        layer.setOpacity(layerVisible ? 0.8 : 0)
      );
    }

    if (sideBySideControlRef.current) {
      if (layerVisible) {
        sideBySideControlRef.current.addTo(map);
      } else {
        sideBySideControlRef.current.remove();
      }
    }
  }, [layerVisible, map]);

  return null;
};

export default GeotiffLayerSideBySide;
